import { logNewRelicPageAction } from '@makemydeal/dr-activities-common';

// interfaces/types
import type { UsabilityContext } from './usabilityTelemetry';

// `logNewRelicPageAction` does not guard its own `addPageAction`. These events ride on dispatches followed
// immediately by `onClose()` / `openSlideOut(...)`, so an escaping throw would skip those. Never emit
// outside this wrapper.
const emit = (eventName: string, attributes: Record<string, unknown>): void => {
    try {
        logNewRelicPageAction(eventName, attributes);
    } catch {
        // Best-effort by design.
    }
};

export const VAUTO_APPRAISAL_EVENTS = {
    launched: 'MV:VAutoAppraisal:Launched',
    slideOutLoaded: 'MV:VAutoAppraisal:SlideOutLoaded',
    messageReceived: 'MV:VAutoAppraisal:MessageReceived',
    messageRejected: 'MV:VAutoAppraisal:MessageRejected',
    acvFetchCompleted: 'MV:VAutoAppraisal:AcvFetchCompleted',
    acvSyncOutcome: 'MV:VAutoAppraisal:AcvSyncOutcome'
} as const;

export type VAutoAppraisalLaunchMode = 'slideOut' | 'newTab';

export type VAutoAppraisalLaunchType = 'new' | 'existing';

export type VAutoAppraisalMessageType = 'saved' | 'save' | 'finalize';

/** `missingAppraisalId` = trusted origin, Save/Finalize named, no id — the panel silently never closes. */
export type VAutoAppraisalMessageRejectionReason = 'originMismatch' | 'missingAppraisalId';

export type VAutoAcvFetchOutcome = 'success' | 'failure';

export type VAutoAcvFetchTrigger = 'dealLoad' | 'slideOutClose';

export interface VAutoAppraisalLaunchedEventData {
    launchMode: VAutoAppraisalLaunchMode;
    launchType: VAutoAppraisalLaunchType;
    appraisalStatus: string;
    tradeInIndex: number;
    hasVin: boolean;
}

export const trackVAutoAppraisalLaunched = (data: VAutoAppraisalLaunchedEventData, context: UsabilityContext): void => {
    emit(VAUTO_APPRAISAL_EVENTS.launched, {
        launchMode: data.launchMode,
        launchType: data.launchType,
        appraisalStatus: data.appraisalStatus,
        tradeInIndex: data.tradeInIndex,
        hasVin: data.hasVin,
        ...context
    });
};

export interface VAutoAppraisalSlideOutLoadedEventData {
    launchType: VAutoAppraisalLaunchType;
    tradeInIndex: number;
    iframeLoadMs: number;
    durationMs?: number;
}

export const trackVAutoAppraisalSlideOutLoaded = (data: VAutoAppraisalSlideOutLoadedEventData, context: UsabilityContext): void => {
    emit(VAUTO_APPRAISAL_EVENTS.slideOutLoaded, {
        launchType: data.launchType,
        tradeInIndex: data.tradeInIndex,
        iframeLoadMs: data.iframeLoadMs,
        ...(typeof data.durationMs === 'number' ? { durationMs: data.durationMs } : {}),
        ...context
    });
};

export interface VAutoAppraisalMessageReceivedEventData {
    messageType: VAutoAppraisalMessageType;
    dwellMs?: number;
}

export const trackVAutoAppraisalMessageReceived = (
    data: VAutoAppraisalMessageReceivedEventData,
    context: UsabilityContext
): void => {
    emit(VAUTO_APPRAISAL_EVENTS.messageReceived, {
        messageType: data.messageType,
        ...(typeof data.dwellMs === 'number' ? { dwellMs: data.dwellMs } : {}),
        ...context
    });
};

// An origin is only ever scheme://host[:port] — no path or query — so both are safe to record verbatim.
export interface VAutoAppraisalMessageRejectedEventData {
    reason: VAutoAppraisalMessageRejectionReason;
    receivedOrigin: string;
    expectedOrigin: string;
}

export const trackVAutoAppraisalMessageRejected = (
    data: VAutoAppraisalMessageRejectedEventData,
    context: UsabilityContext
): void => {
    emit(VAUTO_APPRAISAL_EVENTS.messageRejected, {
        reason: data.reason,
        receivedOrigin: data.receivedOrigin,
        expectedOrigin: data.expectedOrigin,
        ...context
    });
};

export interface VAutoAcvFetchCompletedEventData {
    outcome: VAutoAcvFetchOutcome;
    trigger: VAutoAcvFetchTrigger;
    appraisalCount: number;
    durationMs?: number;
    errorStatus?: number;
    errorMessage?: string;
}

export const trackVAutoAcvFetchCompleted = (data: VAutoAcvFetchCompletedEventData, context: UsabilityContext): void => {
    emit(VAUTO_APPRAISAL_EVENTS.acvFetchCompleted, {
        outcome: data.outcome,
        trigger: data.trigger,
        appraisalCount: data.appraisalCount,
        ...(typeof data.durationMs === 'number' ? { durationMs: data.durationMs } : {}),
        ...(typeof data.errorStatus === 'number' ? { errorStatus: data.errorStatus } : {}),
        ...(data.errorMessage ? { errorMessage: data.errorMessage } : {}),
        ...context
    });
};

export interface VAutoAcvSyncOutcomeEventData {
    trigger: VAutoAcvFetchTrigger;
    tradeInIndex: number;
    /** Was the write attempted. This — NOT `acvChanged` — is the "did the sync work" metric. */
    overrideApplied: boolean;
    /** Did the ACV move. False for a healthy write that landed on the value already held. */
    acvChanged: boolean;
    acvBefore: number;
    acvAfter: number;
    appraisalValue: number;
    appraisalStatus: string;
    acvOverridePermitted: boolean;
    hasOfferCreatedOn: boolean;
    hasAppraisalUpdatedOn: boolean;
}

export const trackVAutoAcvSyncOutcome = (data: VAutoAcvSyncOutcomeEventData, context: UsabilityContext): void => {
    emit(VAUTO_APPRAISAL_EVENTS.acvSyncOutcome, {
        trigger: data.trigger,
        tradeInIndex: data.tradeInIndex,
        overrideApplied: data.overrideApplied,
        acvChanged: data.acvChanged,
        acvBefore: data.acvBefore,
        acvAfter: data.acvAfter,
        appraisalValue: data.appraisalValue,
        appraisalStatus: data.appraisalStatus,
        acvOverridePermitted: data.acvOverridePermitted,
        hasOfferCreatedOn: data.hasOfferCreatedOn,
        hasAppraisalUpdatedOn: data.hasAppraisalUpdatedOn,
        ...context
    });
};
